import {
  GET_APP_QUICK_SEARCH,
  GET_PRODUCT_LIST,
  getProductDeliveryTimeLeft,
  GET_MASTHEAD_DATA,
  GET_HOME_FEED,
  buildHomeBatchApiPaths,
  GET_HOME_CMS_BATCH_API_URL,
  HOME_CMS_BATCH_DEFAULT_STORE,
  HOME_CMS_BATCH_SERVER_CACHE_TTL_MS,
} from "./constant";
import { mapHomeBatchApiResponse } from "./cmsBatchMap";
import { InterceptorClient } from "../../interceptor/interceptorClient";
import { withServerCache } from "../../utils/cache-layer/serverCache";
import { isInterceptorClientErrorShape, isLikelyCmsBatchPayload } from "../../utils/cmsBatchClientGuards";

/**
 * Returns null when the gateway interceptor surfaced an HTTP error shape instead of feed JSON.
 *
 * @param {unknown} response
 * @returns {object|null}
 */
export function normalizePageFeedResponse(response) {
  if (!response || typeof response !== "object" || isInterceptorClientErrorShape(response)) {
    return null;
  }

  return response;
}

const basicHeaders = {
  "Access-Control-Allow-Credentials": "true",
  "Access-Control-Allow-Headers": "Content-Type",
  "Content-Type": "text/html;charset=UTF-8",
  "X-made-with": "Made with <3 in India",
};

/**
 * Builds gateway headers for homepage search feed/masthead calls.
 * @param {boolean} isMobile - Device class for x-device-type
 * @returns {Record<string, string>}
 */
export function buildHomepageSearchGatewayHeaders(isMobile) {
  return {
    ...basicHeaders,
    "x-device-type": isMobile ? "mobile" : "desktop",
  };
}

/**
 * Shared fnpId / email / pincode query fields for homepage gateway requests.
 * @param {string} [fnpId]
 * @param {string} [email]
 * @param {string} [pincode]
 * @returns {{ fnpId: string, email: string, pincode: string }}
 */
function buildHomepageIdentityParams(fnpId, email, pincode) {
  return {
    ...(pincode && { pincode }),
    ...(email && { email }),
    ...(fnpId && { fnpId }),
  };
}

/**
 * GET against search gateway for homepage feed or masthead.
 * @param {string} uri - `GET_HOME_FEED` or `GET_MASTHEAD_DATA`
 * @param {Record<string, string>} params - Query string fields
 * @param {boolean} isMobileDevice - Drives `x-device-type` header
 * @returns {Promise<unknown>}
 */
async function requestHomepageGateway(uri, params, isMobileDevice) {
  const headers = buildHomepageSearchGatewayHeaders(isMobileDevice);
  return InterceptorClient.getGatewayInstance().get(uri, false, params, headers);
}

/**
 * Fetches the dynamic home page feed (desktop feed or mobile masthead).
 * @param {Object} args - Request arguments
 * @param {boolean} args.isMobile - Mobile vs desktop endpoint and platform
 * @param {string} [args.storeName] - Store name for mobile masthead (selects the brand tab feed)
 * @param {string} [args.fnpId] - User / experiment id (fnpId query)
 * @param {string} [args.email] - Email query
 * @param {string} [args.pincode] - Pincode query
 * @param {boolean} [args.campaignWidgetsOnly] - When true, returns personalized campaign feed (client-only)
 * @param {string} [args.pageType="homepage"] - Feed context (`homepage`, `search`, etc.)
 * @returns {Promise<object|null>} Feed/masthead JSON or null on failure
 */
export const getPageFeedAPI = async ({
  isMobile,
  storeName,
  fnpId,
  email,
  pincode,
  campaignWidgetsOnly,
  pageType = "homepage",
}) => {
  try {
    const identity = buildHomepageIdentityParams(fnpId, email, pincode);
    if (isMobile) {
      const params = {
        platform: "mobile",
        pageType,
        ...(storeName && { storeName }),
        ...identity,
        ...(campaignWidgetsOnly && { campaignWidgetsOnly: true }),
      };
      return normalizePageFeedResponse(await requestHomepageGateway(GET_MASTHEAD_DATA, params, true));
    }
    const params = {
      pageType,
      platform: "desktop",
      ...identity,
      ...(campaignWidgetsOnly && { campaignWidgetsOnly: true }),
    };
    return normalizePageFeedResponse(await requestHomepageGateway(GET_HOME_FEED, params, false));
  } catch (error) {
    return null;
  }
};

/**
 * Feed for the default (empty) search panel — `/search/v1/homepage/feed` with `pageType=search`.
 *
 * @param {Object} args
 * @param {boolean} args.isMobile - Query `platform=mobile` vs `desktop`
 * @param {string} args.geoId - Catalog id (domestic and international)
 * @returns {Promise<object|null>}
 */
export const getDefaultSearchPageFeedAPI = async ({ isMobile, geoId }) => {
  try {
    const params = {
      pageType: "search",
      platform: isMobile ? "mobile" : "desktop",
      geoId,
    };
    return normalizePageFeedResponse(await requestHomepageGateway(GET_HOME_FEED, params, isMobile));
  } catch (error) {
    return null;
  }
};

/**
 * API for the data for Gift Finder
 * @returns
 */
export const getQuickSearch = async () => {
  return await InterceptorClient.getGatewayInstance().get(`${GET_APP_QUICK_SEARCH}`);
};

/**
 * API for the list of products
 */
export const getProductList = async () => {
  return await InterceptorClient.getGatewayInstance().get(`${GET_PRODUCT_LIST()}`);
};

/**
 * Fetches the remaining time for product delivery from the API.
 *
 * This asynchronous function sends a request to the API endpoint returned by `getProductDeliveryTimeLeft()`
 * and retrieves the remaining delivery time for a product.
 *
 * @async
 * @function
 * @returns {Promise<Object>} A promise that resolves to the JSON response from the API, containing the delivery time left.
 * @throws {Error} Will throw an error if the fetch request fails.
 */
export const getDeliveryTimeLeft = async ({ productId, pincode }) => {
  return await InterceptorClient.getFTSInstance(process.env.NEXT_PUBLIC_BASE_URL).get(
    `${getProductDeliveryTimeLeft(productId, pincode)}`,
  );
};

/**
 * Fetches raw homepage CMS batch from the CMS server gateway (`NEXT_PUBLIC_CMS_SERVER_API_GATEWAY`).
 *
 * @param {boolean} isMobile - Whether the request is from mobile
 * @param {string} [catalogId=""] - Catalog id, kept for cache keying
 * @param {string} [store] - Msite store slug; ignored on desktop
 * @returns {Promise<Object>} Batch response map keyed by CMS path, or `{}` on failure
 */
export const getHomeCmsBatchApi = async (isMobile, catalogId = "", store) => {
  try {
    const pathsArray = buildHomeBatchApiPaths({ isMobile, catalogId });
    const storeForUrl = isMobile ? store || HOME_CMS_BATCH_DEFAULT_STORE : undefined;
    const relativeUrl = GET_HOME_CMS_BATCH_API_URL(pathsArray, storeForUrl);
    const response = await InterceptorClient.getServerCMSInstance().get(relativeUrl, false, null, {
      ...basicHeaders,
      "x-device-type": isMobile ? "mobile" : "desktop",
      "x-domain": "www.fnp.com",
    });

    if (isInterceptorClientErrorShape(response)) {
      throw new Error(`Home CMS batch HTTP ${response.status}`);
    }
    if (!isLikelyCmsBatchPayload(response)) {
      return {};
    }
    return response;
  } catch (error) {
    console.log("Error fetching home CMS batch API", error);
    return {};
  }
};

/**
 * Fetches homepage CMS batch, maps and JSON-parses fields, and caches the result per process.
 *
 * @param {Object} args
 * @param {boolean} args.isMobile
 * @param {string} [args.catalogId]
 * @param {string} [args.store] - Msite store slug from URL (`/store/[slug]`) or default `fnp`
 * @returns {Promise<Object>} Mapped homepage batch with the `parsed` bundle
 */
export const getHomeCmsBatchMapped = async ({ isMobile, catalogId = "", store }) => {
  const storeForCache = isMobile ? store || HOME_CMS_BATCH_DEFAULT_STORE : null;
  const cacheKey = `home:cms-batch:${isMobile ? "m" : "d"}:${catalogId || "-"}${storeForCache ? `:${storeForCache}` : ""}`;
  try {
    return await withServerCache(
      cacheKey,
      async () => {
        const raw = await getHomeCmsBatchApi(isMobile, catalogId, storeForCache ?? undefined);
        return mapHomeBatchApiResponse(raw, { isMobile });
      },
      HOME_CMS_BATCH_SERVER_CACHE_TTL_MS,
    );
  } catch (error) {
    console.log("Error fetching home mapped CMS batch", error);
    return mapHomeBatchApiResponse({}, { isMobile });
  }
};

export { mapHomeBatchApiResponse };
